// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import { useMaterialUIController } from "context";

// PMS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { Stack } from "@mui/material";

function IntegrationInfoCard({
  color,
  title,
  icon,
  description,
  isEnable,
  btnText,
  onClick,
  btnText2 = "",
  onClick2 = () => {},
}) {
  const [controller] = useMaterialUIController();
  const { sidenavColor, darkMode } = controller;

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "box-shadow 0.3s ease, transform 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: darkMode
            ? "0 8px 24px rgba(0,0,0,0.5)"
            : "0 8px 24px rgba(0,0,0,0.12)",
        },
      }}
    >
      <MDBox p={2.5} display="flex" flexDirection="column" height="100%">
        {/* Header Row */}
        <MDBox display="flex" alignItems="flex-start" justifyContent="space-between" mb={1.5}>
          <MDBox display="flex" alignItems="center" gap={1.5}>
            <MDBox
              variant="gradient"
              bgColor={isEnable ? sidenavColor : "secondary"}
              color={isEnable ? (color === "light" ? "dark" : "white") : "white"}
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="3rem"
              height="3rem"
              sx={{
                transition: "background-color 0.3s ease",
                flexShrink: 0,
              }}
            >
              <Icon fontSize="medium" color="inherit">
                {icon}
              </Icon>
            </MDBox>
            <MDBox>
              <MDTypography variant="h6" fontWeight="bold" lineHeight={1.2}>
                {title}
              </MDTypography>
              <Tooltip
                title={isEnable ? "Integration is active" : "Integration is inactive"}
                placement="top"
                arrow
              >
                <Chip
                  label={isEnable ? "Active" : "Inactive"}
                  size="small"
                  sx={{
                    mt: 0.4,
                    height: "18px",
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    letterSpacing: "0.03em",
                    backgroundColor: isEnable
                      ? "rgba(76, 175, 80, 0.15)"
                      : darkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.06)",
                    color: isEnable
                      ? "#4caf50"
                      : darkMode
                      ? "rgba(255,255,255,0.5)"
                      : "rgba(0,0,0,0.4)",
                    border: isEnable
                      ? "1px solid rgba(76,175,80,0.4)"
                      : "1px solid rgba(150,150,150,0.2)",
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
              </Tooltip>
            </MDBox>
          </MDBox>

          {/* Live indicator dot */}
          {isEnable && (
            <MDBox
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "#4caf50",
                flexShrink: 0,
                mt: 0.5,
                boxShadow: "0 0 0 2px rgba(76,175,80,0.25)",
                animation: "pulseDot 2s infinite",
                "@keyframes pulseDot": {
                  "0%": { boxShadow: "0 0 0 0 rgba(76,175,80,0.5)" },
                  "70%": { boxShadow: "0 0 0 6px rgba(76,175,80,0)" },
                  "100%": { boxShadow: "0 0 0 0 rgba(76,175,80,0)" },
                },
              }}
            />
          )}
        </MDBox>

        {/* Description */}
        {description && (
          <>
            <Divider sx={{ my: 1, opacity: 0.4 }} />
            <MDTypography
              variant="caption"
              color="secondary"
              sx={{
                lineHeight: 1.5,
                flexGrow: 1,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {description}
            </MDTypography>
          </>
        )}

        {/* Action Buttons */}
        <MDBox display="flex" justifyContent="flex-end" alignItems="center" mt={2}>
          <Stack direction="row" spacing={1}>
            {btnText2 && (
              <MDButton
                variant="outlined"
                size="small"
                color={sidenavColor}
                onClick={onClick2}
                startIcon={<Icon sx={{ fontSize: "14px !important" }}>edit</Icon>}
              >
                {btnText2}
              </MDButton>
            )}
            <MDButton
              variant={isEnable ? "outlined" : "gradient"}
              size="small"
              color={isEnable ? "error" : sidenavColor}
              onClick={onClick}
              startIcon={
                <Icon sx={{ fontSize: "14px !important" }}>
                  {isEnable ? "power_settings_new" : "power"}
                </Icon>
              }
            >
              {btnText}
            </MDButton>
          </Stack>
        </MDBox>
      </MDBox>
    </Card>
  );
}

// Setting default values for the props of IntegrationInfoCard
IntegrationInfoCard.defaultProps = {
  color: "info",
  description: "",
  isEnable: false,
  btnText2: "",
  onClick2: () => {},
};

// Typechecking props for the IntegrationInfoCard
IntegrationInfoCard.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
  ]),
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  icon: PropTypes.node.isRequired,
  isEnable: PropTypes.bool,
  btnText: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  btnText2: PropTypes.string,
  onClick2: PropTypes.func,
};

export default IntegrationInfoCard;
